import {
  DotsThree,
  PencilSimpleLine,
  PushPin,
  Trash,
} from '@phosphor-icons/react'
import React, { useState } from 'react'

import AlertDialog from '@/components/shared/ui/AlertDialog/AlertDialog'
import Button from '@/components/shared/ui/Button'
import Dropdown from '@/components/shared/ui/Dropdown'
import {
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from '@/components/shared/ui/Dropdown/Dropdown'
import { useTranslations } from '@/hooks/useTranslations'
import {
  type IResourceBaseData,
  deleteResourceComment,
  updateResourceComment,
} from '@/models/Resource'
import type { IComment } from '@/types/comment'
import toaster from '@/utils/toast'

interface IPropTypes extends IResourceBaseData {
  comment: IComment
  onEdit: () => void
  onUpdate: (comment: IComment) => void
  onDelete: (comment: IComment) => void
}

export default function ResourceCommentAction({
  comment,
  onEdit,
  onUpdate,
  onDelete,
}: IPropTypes) {
  const t = useTranslations('comments.commentItem.actions')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const handleDelete = () =>
    deleteResourceComment(comment.id).then(() => onDelete(comment))

  const handlePin = () =>
    updateResourceComment(comment.id, {
      pinned: !comment.pinned,
    })
      .then(onUpdate)
      .catch((err) => {
        toaster.error({ message: err.message })
      })
  const openDeleteConfirmation = () => setDeleteDialogOpen(true)

  if (!comment.can_edit && !comment.can_delete && !comment.can_pin) return null

  return (
    <>
      <Dropdown>
        <DropdownTrigger>
          <Button fab size='xs' variant='naked'>
            <DotsThree className='h-4 w-4' weight='bold' />
          </Button>
        </DropdownTrigger>
        <DropdownContent>
          {comment.can_edit && (
            <DropdownItem
              onSelect={onEdit}
              itemValue={t('buttons.edit', { ignorePrefix: true })}
              icon={<PencilSimpleLine />}
            />
          )}
          {comment.can_pin && (
            <DropdownItem
              onSelect={handlePin}
              icon={<PushPin />}
              itemValue={t(`buttons.${comment.pinned ? 'unpin' : 'pin'}`, {
                ignorePrefix: true,
              })}
            />
          )}
          {comment.can_delete && (
            <DropdownItem
              variant='destructive'
              onSelect={openDeleteConfirmation}
              itemValue={t('buttons.delete', { ignorePrefix: true })}
              icon={<Trash />}
            />
          )}
        </DropdownContent>
      </Dropdown>
      <AlertDialog
        open={deleteDialogOpen}
        type='danger'
        title={t('delete.alert.title')}
        description={t('delete.alert.description')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      ></AlertDialog>
    </>
  )
}
